import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, FloppyDisk, Archive, Trash, Wallet, ChartBar } from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ArchiveSessionDialog from "@/components/ArchiveSessionDialog";
import DeleteSchoolDialog from "@/components/DeleteSchoolDialog";

function AddAdminDialog({ open, onOpenChange, schoolId, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", password: "", phone: "" });
      setError("");
    }
  }, [open]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/super-admin/schools/${schoolId}/admins`, cleanPayload(form));
      toast.success("School admin added");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add school admin</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="add-admin-name" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input required type="email" value={form.email} onChange={update("email")} className="mt-1.5" data-testid="add-admin-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
          </div>
          <div>
            <Label>Password *</Label>
            <Input
              required
              minLength={8}
              type="text"
              value={form.password}
              onChange={update("password")}
              className="mt-1.5 font-mono"
              data-testid="add-admin-password"
            />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="add-admin-submit">
              {submitting ? "Adding…" : "Add admin"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModuleFlagsPanel({ school, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  const enabled = !!school?.special_collections_enabled;
  const reconEnabled = !!school?.collection_recon_report_enabled;

  const toggle = async () => {
    const next = !enabled;
    const action = next ? "enable" : "disable";
    const confirmMsg = next
      ? "Enable Special Collections for this school? A default catalog (Admission Fee, TC Fee, Fine, Old Dues) will be seeded if empty."
      : "Disable Special Collections for this school? Existing receipts will be preserved but the module will be hidden from the sidebar.";
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/super-admin/schools/${school.id}/special-collections/${action}`,
      );
      toast.success(
        next ? "Special Collections enabled" : "Special Collections disabled",
      );
      onChanged?.({ ...school, special_collections_enabled: !!data?.special_collections_enabled });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const toggleRecon = async () => {
    const next = !reconEnabled;
    const action = next ? "enable" : "disable";
    const confirmMsg = next
      ? "Enable the Collection & Reconciliation Report for this school? School admins and cashiers will see a downloadable XLSX/CSV report card on /reports."
      : "Disable the Collection & Reconciliation Report for this school? The card + sidebar link will vanish; historical data remains untouched.";
    if (!window.confirm(confirmMsg)) return;
    setReconBusy(true);
    try {
      const { data } = await api.post(
        `/super-admin/schools/${school.id}/collection-recon/${action}`,
      );
      toast.success(next ? "Recon Report enabled" : "Recon Report disabled");
      onChanged?.({
        ...school,
        collection_recon_report_enabled: !!data?.collection_recon_report_enabled,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setReconBusy(false);
  };

  return (
    <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="school-modules-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xl font-medium font-heading">Modules</h3>
          <p className="text-xs text-slate-500 mt-1">
            Optional feature modules. Toggle per tenant.
          </p>
        </div>
      </div>

      <div
        className="flex items-start gap-4 py-3 px-3 border border-slate-100 rounded-md"
        data-testid="module-row-special-collections"
      >
        <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
          <Wallet size={20} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            Special Collections
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                enabled
                  ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
                  : "text-slate-500 bg-slate-100"
              }`}
              data-testid="sc-status-badge"
            >
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Independent cash ledger for non-monthly fees (Admission, TC, Fines,
            Old Dues). Fully isolated from the Monthly Fee engine — does not
            affect dues, defaulters, or accrual reports.
          </p>
        </div>
        <Button
          size="sm"
          variant={enabled ? "outline" : "default"}
          onClick={toggle}
          disabled={busy}
          className={enabled ? "" : "bg-blue-900 hover:bg-blue-800"}
          data-testid="toggle-sc-btn"
        >
          {busy ? "…" : enabled ? "Disable" : "Enable"}
        </Button>
      </div>

      {/* Phase 30L — Collection & Reconciliation Report toggle */}
      <div
        className="flex items-start gap-4 py-3 px-3 border border-slate-100 rounded-md mt-3"
        data-testid="module-row-collection-recon"
      >
        <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
          <ChartBar size={20} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            Collection &amp; Reconciliation Report
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                reconEnabled
                  ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
                  : "text-slate-500 bg-slate-100"
              }`}
              data-testid="recon-status-badge"
            >
              {reconEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Downloadable XLSX/CSV register of fee collections for daily
            accounts closing. Read-only, feature-flag gated, and additive —
            does not affect existing payment flow or KPI math.
          </p>
        </div>
        <Button
          size="sm"
          variant={reconEnabled ? "outline" : "default"}
          onClick={toggleRecon}
          disabled={reconBusy}
          className={reconEnabled ? "" : "bg-blue-900 hover:bg-blue-800"}
          data-testid="toggle-recon-btn"
        >
          {reconBusy ? "…" : reconEnabled ? "Disable" : "Enable"}
        </Button>
      </div>
    </div>
  );
}

export default function SchoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [archiveSession, setArchiveSession] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openAddAdmin, setOpenAddAdmin] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, u, ss] = await Promise.all([
        api.get(`/super-admin/schools/${id}`),
        api.get(`/super-admin/schools/${id}/users`),
        api.get(`/super-admin/schools/${id}/sessions`),
      ]);
      setSchool(s.data);
      setForm({
        school_name: s.data.school_name || "",
        address: s.data.address || "",
        phone: s.data.phone || "",
        email: s.data.email || "",
        upi_id: s.data.upi_id || "",
        receipt_footer: s.data.receipt_footer || "",
        theme_color: s.data.theme_color || "#1E3A8A",
      });
      setUsers(u.data);
      setSessions(ss.data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/super-admin/schools/${id}`, cleanPayload(form));
      setSchool(data);
      toast.success("Branding updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (error || !school) {
    return <div className="text-sm text-red-600">{error || "School not found"}</div>;
  }

  return (
    <div className="space-y-6" data-testid="school-detail-page">
      <div>
        <Link to="/schools" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-3">
          <ArrowLeft size={14} /> All schools
        </Link>
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="h-14 w-14 rounded-md flex items-center justify-center text-white text-xl font-bold font-heading"
            style={{ backgroundColor: school.theme_color }}
          >
            {school.school_name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading">{school.school_name}</h1>
            <div className="text-sm text-slate-500 mt-1">
              ID <span className="font-mono text-xs">{school.id}</span> ·{" "}
              <span className={school.status === "active" ? "text-emerald-700" : "text-slate-500"}>
                {school.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="lg:col-span-2 p-5 border border-slate-200 bg-white rounded-lg space-y-4" data-testid="branding-form">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-medium font-heading">Branding & contact</h3>
            <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="branding-save-btn">
              <FloppyDisk size={14} /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>School name</Label>
              <Input value={form.school_name} onChange={update("school_name")} className="mt-1.5" data-testid="branding-school-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={update("address")} className="mt-1.5" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.email} onChange={update("email")} className="mt-1.5" />
            </div>
            <div>
              <Label>UPI ID</Label>
              <Input value={form.upi_id} onChange={update("upi_id")} className="mt-1.5" data-testid="branding-upi" />
            </div>
            <div>
              <Label>Theme color</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="color"
                  value={form.theme_color}
                  onChange={update("theme_color")}
                  className="h-10 w-14 rounded border border-slate-200"
                />
                <Input value={form.theme_color} onChange={update("theme_color")} />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Receipt footer</Label>
              <Input value={form.receipt_footer} onChange={update("receipt_footer")} className="mt-1.5" />
            </div>
          </div>
        </form>

        <div className="p-5 border border-slate-200 bg-white rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-medium font-heading">Users</h3>
            <Button size="sm" variant="outline" onClick={() => setOpenAddAdmin(true)} className="gap-1.5" data-testid="add-admin-btn">
              <Plus size={14} /> Admin
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">School admins and teachers</p>
          <div className="mt-3 space-y-2" data-testid="school-users-list">
            {users.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 text-center">No users yet.</div>
            ) : (
              users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                    {(u.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {u.role.replace("_", " ")}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <AddAdminDialog
        open={openAddAdmin}
        onOpenChange={setOpenAddAdmin}
        schoolId={id}
        onCreated={(u) => setUsers((arr) => [u, ...arr])}
      />

      {/* Phase X — Module feature flags (per-tenant) */}
      <ModuleFlagsPanel school={school} onChanged={(updated) => setSchool(updated)} />

      {/* Phase 16 — Sessions panel (super-admin can archive / wipe / unarchive) */}
      <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="school-sessions-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xl font-medium font-heading">Academic sessions</h3>
            <p className="text-xs text-slate-500 mt-1">
              Archive a session to lock writes (reversible). Flagged test sessions can be permanently wiped.
            </p>
          </div>
        </div>
        <div className="space-y-1.5" data-testid="school-sessions-list">
          {sessions.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">No sessions yet.</div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-3 py-2.5 px-3 border border-slate-100 rounded-md hover:bg-slate-50"
                data-testid={`school-session-row-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                    {s.name}
                    {s.is_active && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    )}
                    {s.is_archived && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                        Archived
                      </span>
                    )}
                    {s.is_test_data && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-purple-800 bg-purple-100 px-1.5 py-0.5 rounded">
                        Test data
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.start_date} → {s.end_date}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setArchiveSession(s)}
                  data-testid={`school-session-manage-${s.id}`}
                >
                  <Archive size={14} /> {s.is_archived ? "Manage" : "Archive…"}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      <ArchiveSessionDialog
        open={!!archiveSession}
        onOpenChange={(o) => !o && setArchiveSession(null)}
        session={archiveSession}
        schoolId={id}
        onChanged={load}
      />

      {/* Danger zone — hard delete this school */}
      <div className="p-5 border-2 border-rose-200 bg-rose-50/30 rounded-lg" data-testid="school-danger-zone">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-rose-900 font-heading">Danger zone</h3>
            <p className="text-xs text-rose-700 mt-1 max-w-xl">
              Permanently delete this school and all its data — sessions, classes, students, payments, audit
              logs, and school staff accounts. This cannot be undone.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setOpenDelete(true)}
            className="border-rose-300 text-rose-800 hover:bg-rose-100 gap-1.5"
            data-testid="school-delete-btn"
          >
            <Trash size={14} weight="fill" /> Delete school
          </Button>
        </div>
      </div>

      <DeleteSchoolDialog
        open={openDelete}
        onOpenChange={setOpenDelete}
        school={school}
        onDeleted={() => navigate("/schools")}
      />
    </div>
  );
}
